// API client for the Burnish Visualizer Modal backend.
//
// The backend exposes two endpoints we care about:
//   POST /submit            -> { job_id, call_id }
//   GET  /status/{call_id}  -> StatusResponse
//
// Default API base is the deployed Modal web app. Override with
// ?api=https://other.host on the URL for staging / local Modal sandbox.

const DEFAULT_API_BASE = "https://bcrumrin--burnish-visualizer-web.modal.run";

export function getApiBase(): string {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const override = params.get("api");
    if (override) return override.replace(/\/$/, "");
  }
  return DEFAULT_API_BASE;
}

export interface ManifestEntry {
  rel_path: string;
  size_bytes: number;
  url: string;
}

export interface SubmitResponse {
  job_id: string;
  call_id: string;
}

export type StatusResponse =
  | { state: "running" }
  | { state: "expired" }
  | {
      state: "done";
      manifest: ManifestEntry[];
      r2_prefix: string;
      render_seconds?: number;
      upload_seconds?: number;
    }
  | { state: "failed"; error: string; stdout?: string; stderr?: string };

export interface SubmitInput {
  audio: File;
  cover: File;
  meta: string; // JSON-encoded SongMetadata
  visualImages: { name: string; file: File }[]; // names are meta-relative paths
  preset?: string;
  bpm?: number;
  shortStarts?: string;
  only?: "full" | "shorts" | "canvas";
  fullPipeline: boolean; // true = release generate, false = render
  concurrency: number;
}

export async function submit(input: SubmitInput): Promise<SubmitResponse> {
  const fd = new FormData();
  fd.append("audio", input.audio);
  fd.append("cover", input.cover);
  fd.append("meta", input.meta);
  fd.append(
    "visual_image_names",
    JSON.stringify(input.visualImages.map((vi) => vi.name)),
  );
  for (const vi of input.visualImages) {
    fd.append("visual_images", vi.file, vi.name);
  }
  if (input.preset) fd.append("preset", input.preset);
  if (input.bpm !== undefined) fd.append("bpm", String(input.bpm));
  if (input.shortStarts) fd.append("short_starts", input.shortStarts);
  if (input.only) fd.append("only", input.only);
  fd.append("full_pipeline", input.fullPipeline ? "true" : "false");
  fd.append("concurrency", String(input.concurrency));

  const resp = await fetch(`${getApiBase()}/submit`, {
    method: "POST",
    body: fd,
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Submit failed (${resp.status}): ${text}`);
  }
  return resp.json();
}

export async function getStatus(callId: string): Promise<StatusResponse> {
  const resp = await fetch(
    `${getApiBase()}/status/${encodeURIComponent(callId)}`,
  );
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Status check failed (${resp.status}): ${text}`);
  }
  return resp.json();
}

// Poll /status until the call leaves the "running" state. Calls
// onUpdate with each non-terminal poll so the UI can show liveness
// (elapsed seconds, etc.). Resolves with the terminal status.
export async function pollUntilDone(
  callId: string,
  opts: {
    intervalMs?: number;
    onUpdate?: (status: StatusResponse) => void;
  } = {},
): Promise<StatusResponse> {
  const interval = opts.intervalMs ?? 5000;
  while (true) {
    const status = await getStatus(callId);
    if (status.state !== "running") return status;
    opts.onUpdate?.(status);
    await new Promise((r) => setTimeout(r, interval));
  }
}
